<template>
  <div>
    <section-header :ressource="ressource"
                    :isExtended="true"
                    :dialog="true"
                    @closeDialog=closeDialog
                    ref="ACTUALITE">
    </section-header>
    <div class='py-2'>
      <edit-field :value="actualite.label"
                  :editFieldInfo="labelFieldInfo"
                  @updated="setLabel">
      </edit-field>

      <v-row class="m-0">
        <div class="col px-0">
          <edit-field :value="actualite.url"
                      :editFieldInfo="urlFieldInfo"
                      @updated="setUrl">
          </edit-field>
        </div>
        <div v-if="actualite.url" class="col-1 d-flex align-center justify-end">
          <v-icon @click.stop="openLink()" size="25">fas fa-arrow-up-right-from-square</v-icon>
        </div>
      </v-row>
      <html-field :label="ressourceTitle"
                  :value="actualite.title"
                  @updated="setTitle">
      </html-field>

      <v-file-input v-model="file"
                    accept="image/*"
                    :label="uploadImageLabel"
                    @change="createImage">
      </v-file-input>
      <v-img :src='actualite.image'></v-img>

      <html-field :label="ressourceDescription"
                  :value="actualite.description"
                  @updated="setDescription">
      </html-field>

      <html-field :label="ressourceDescription"
                  :value="actualite.shortDescription"
                  @updated="setShortDescription">
      </html-field>

    </div>
    <div class="d-flex flex-row-reverse">
      <loader-button class="ms-2"
                      type='button'
                      :label="saveLabel"
                      :disabled="!updated"
                      @callback="save">
      </loader-button>
    </div>
  </div>
</template>
<script lang="ts">
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import { FwkButtons, FwkControls, FwkSections, RessourceFwkButton, RessourceFwkControl, RessourceFwkSection } from '@/ressources/globalRessources';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import EditField from '@/framework/base/components/EditField.vue';
import { FieldInfo, EditFieldType, EditFieldTheme } from '@/framework/base/components/FieldInfo';
import HtmlAttrib from '@/generated/classes/HtmlAttrib';
import HtmlField from '@/framework/base/components/HtmlField.vue';
import Actualite from '@/extend/classes/Actualite';
import { storeActualite } from '@/generated/stores';
import { ListFilter } from '@/extend/classes/ListData';
import { defineComponent, type PropType } from "vue";

export default defineComponent({
  components: {
    LoaderButton,
    SectionHeader,
    EditField,
    HtmlField,
  },
  props: {
    actualite: {
      type: Object as PropType<Actualite>,
      required: true
    }
  },
  created(): void {
    const allData = new ListFilter();
    allData.pageLimit = -1;
    storeActualite.registerActualite(allData);
    this.refActualite = Actualite.fromJson(this.refActualite);
    if (!this.refActualite.title) {
      this.refActualite.title = "";
      if (this.refActualite.titleHtmlId > 0) {
        HtmlAttrib.get(this.refActualite.titleHtmlId).then((res) => {
          if (res.html != null) {
            this.refActualite.title = res.html;
          }
        });
      }
    }
    if (this.refActualite.description == null) {
      this.refActualite.description = "";
      if (this.refActualite.descriptionHtmlId > 0) {
        HtmlAttrib.get(this.refActualite.descriptionHtmlId).then((res) => {
          if (res.html != null) {
            this.refActualite.description = res.html;
          }
        });
      }
    }
    if (this.refActualite.shortDescription == null) {
      this.refActualite.shortDescription = "";
      if (this.refActualite.shortDescriptionHtmlId > 0) {
        HtmlAttrib.get(this.refActualite.shortDescriptionHtmlId).then((res) => {
          if (res.html != null) {
            this.refActualite.shortDescription = res.html;
          }
        });
      }
    }
  },
  data() {
    const file: File | null = null;
    return {
      refActualite: new Actualite(),
      file,
      updated: false
    };
  },
  computed: {
    saveLabel() {
      return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
    },
    ressource() {
      return RessourceFwkSection(FwkSections.ACTUALITE);
    },
    uploadImageLabel() {
      return RessourceFwkControl(FwkControls.UPLOAD_IMAGE).label;
    },
    ressourceTitle() {
      return RessourceFwkControl(FwkControls.TITLE).label;
    },
    ressourceLabel() {
      return RessourceFwkControl(FwkControls.LABEL).label;
    },
    ressourceDescription() {
      return RessourceFwkControl(FwkControls.DESCRIPTION).label;
    },
    labelFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.LABEL);
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    },
    urlFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.URL);
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    }
  },

  methods: {
    setDescription(value: string) {
      this.refActualite.description = value;
      this.setUpdated();
    },
    setShortDescription(value: string) {
      this.refActualite.shortDescription = value;
      this.setUpdated();
    },
    setTitle(value: string) {
      this.refActualite.title = value;
      this.setUpdated();
    },
    setLabel(value: string) {
      this.refActualite.label = value;
      this.setUpdated();
    },
    setUrl(value: string) {
      this.refActualite.url = value;
      this.setUpdated();
    },
    openLink() {
      if (this.refActualite.url) {
        const link = this.refActualite.url.replace(/(^\w+:|^)\/\//, '');
        window.open("https://" + link, '_blank');
      }
    },
    createImage(file: File) {
      const reader = new FileReader();
      reader.onload = (event) => {
        // @ts-expect-error : generated method
        this.refActualite.image = event.target.result;
      }
      reader.readAsDataURL(file);
      this.setUpdated();
    },
    save(): void {
      this.$emit('updated', this.refActualite);
      this.closeDialog();
    },
    setUpdated() {
      const ref = JSON.stringify(this.refActualite);
      const wizard = JSON.stringify(this.refActualite);
      this.updated = (ref != wizard);
    },
    closeDialog(): void {
      this.$emit('closeDialog');
    }
  }
})

</script>
